import { AxiosResponse } from 'axios';
import { createContext, FC, Fragment, useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../apiClient/apiClient';
import { AuthContextType, User } from './auth.types';

// istanbul ignore next
const AuthContext = createContext<AuthContextType>({
  authToken: null,
  refreshToken: null,
  setAuthToken: () => {},
  setRefreshToken: () => {},
});

export const useUser = (enabled: boolean = false) => {
  return useQuery<AxiosResponse<User>>({
    queryKey: 'user',
    queryFn: () => apiClient.get<User>('/current-user'),
    enabled,
  });
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export const RequireAuth: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authToken, setAuthToken, setRefreshToken } = useAuth();
  const navigate = useNavigate();

  // check for tokens in the url
  const [searchParams] = useSearchParams();

  // make call to useUser on app load to check that we are logged in
  useUser(authToken !== null);

  useEffect(() => {
    const encodedAuthToken = searchParams.get('access_token');
    const encodedRefreshToken = searchParams.get('refresh_token');
    if (encodedAuthToken && encodedRefreshToken) {
      // decode the token from the url
      const accessToken = window.atob(encodedAuthToken);
      const refreshToken = window.atob(encodedRefreshToken);
      // set auth header on ApiClient
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      // store the tokens in localStorage
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      // store the tokens in context
      setAuthToken(accessToken);
      setRefreshToken(refreshToken);
    }
  }, [searchParams]);

  useEffect(() => {
    const localAuthDisabled = process.env.REACT_APP_DISABLE_AUTH_LOCAL;
    // if no token in context
    if (!authToken && !localAuthDisabled) {
      // check for tokens in localStorage
      const storedAuthToken = localStorage.getItem('accessToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      if (storedAuthToken && storedRefreshToken) {
        setAuthToken(storedAuthToken);
        setRefreshToken(storedRefreshToken);
      } else {
        // redirect to login if no tokens in context or local storage
        navigate('/login', { replace: true });
      }
    }
  }, [authToken]);

  return <Fragment>{children}</Fragment>;
};

export const AuthProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  return (
    <AuthContext.Provider value={{ authToken, refreshToken, setAuthToken, setRefreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};
